import { useState, useEffect, useMemo } from 'react';
import { useLots, useCreateLot, useUpdateLot, useDeleteLot } from '../../../../hooks/useLots';
import { useProjects } from '../../../../hooks/useProjects';
import { useContractsWithSubscribersAll } from '../../../../hooks/useContracts';
import { useAllotments } from '../../../../hooks/useAllotments';
import { useConstructionTypes } from '../../../../hooks/useConstructionTypes';
import { useProjectProgressTrackingMonthlyOverview } from '../../../../hooks/useProjectProgressTrackingHistory';
 
import { ITEMS_PER_PAGE, INITIAL_LOT_STATE } from '../constants/lotsConstants';
import { filterLots, getLotReferenceLabel, normalizeLotStatusValue } from '../utils/lotsUtils';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';

export const useLotsTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLot, setSelectedLot] = useState(INITIAL_LOT_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [lotToDelete, setLotToDelete] = useState(null);
  const [selectedAllotment, setSelectedAllotment] = useState('');
  const [selectedReferenceStatus, setSelectedReferenceStatus] = useState('');
  const [selectedContractFilter, setSelectedContractFilter] = useState('');

  const { data: allotments = [] } = useAllotments();
  const { data: allAllotments = [] } = useAllotments({ includeInactive: true });
  const { data: lots = [], isLoading } = useLots({ uuid_allotment: selectedAllotment });
  const { data: allLots = [], isLoading: isAllLotsLoading } = useLots();
  const { data: projects = [] } = useProjects();
  const { data: contracts = [] } = useContractsWithSubscribersAll();
  const { data: constructionTypes = [] } = useConstructionTypes();
  const {
    data: monthlyProgressOverview = null,
    isLoading: isMonthlyProgressOverviewLoading,
  } = useProjectProgressTrackingMonthlyOverview();
  const projectsMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project.uuid_lot) {
        acc[project.uuid_lot] = project;
      }
      return acc;
    }, {});
  }, [projects]);

  const getProject = (lot) => lot?.project || projectsMap[lot?.uuid] || null;

  const selectedProject = useMemo(() => {
    if (selectedLot?.project) {
      return selectedLot.project;
    }
    if (!selectedLot?.uuid) return null;
    return projects.find((proj) => proj.uuid_lot === selectedLot.uuid) || null;
  }, [selectedLot, projects]);

  const selectedContract = useMemo(() => {
    if (selectedLot?.contract) {
      return selectedLot.contract;
    }
    if (!selectedLot?.uuid_contract) return null;
    return contracts.find((contract) => contract.uuid === selectedLot.uuid_contract) || null;
  }, [selectedLot, contracts]);

  const createMutation = useCreateLot();
  const updateMutation = useUpdateLot();
  const deleteMutation = useDeleteLot();

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: lots,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: (collection, term) => {
      const baseFiltered = filterLots(collection, term);
      return baseFiltered.filter((lot) => {
        const referenceState = getLotReferenceLabel({ lot, project: getProject(lot) });
        const matchesReferenceState = !selectedReferenceStatus || referenceState === selectedReferenceStatus;
        const lotContractUuid = lot?.uuid_contract || lot?.contract?.uuid || '';
        const matchesContract = !selectedContractFilter || lotContractUuid === selectedContractFilter;
        return matchesReferenceState && matchesContract;
      });
    },
  });

  const referenceStatusOptions = useMemo(
    () => [
      { value: '', label: 'Todos' },
      { value: 'Constructora Colonial - Obra en progreso', label: 'Constructora Colonial - Obra en progreso' },
      { value: 'No disponible', label: 'No disponible' },
      { value: 'Obra completada', label: 'Obra completada' },
      { value: 'Asignado a Von Der Heyde', label: 'Asignado a Von Der Heyde' },
      { value: 'Asignado a Constructora Colonial', label: 'Asignado a Constructora Colonial' },
      { value: 'Disponible', label: 'Disponible' },
      { value: 'Boleto asociado', label: 'Boleto asociado' },
      { value: 'Asignado a un contrato', label: 'Asignado a un contrato' },
      { value: 'Obra en proceso', label: 'Obra en proceso' },
    ],
    []
  );

  const contractOptions = useMemo(() => {
    const unique = new Map();
    lots.forEach((lot) => {
      const contractUuid = lot?.uuid_contract || lot?.contract?.uuid;
      if (!contractUuid || unique.has(contractUuid)) return;
      const contractFromList = contracts.find((contract) => contract?.uuid === contractUuid);
      const contractNumber = lot?.contract?.contract_number || contractFromList?.contract_number || '';
      unique.set(contractUuid, {
        value: contractUuid,
        label: contractNumber ? `#${contractNumber}` : 'Sin número',
      });
    });
    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [lots, contracts]);

  const handleSelectAllotment = (event) => {
    const value = event.target.value;
    setSelectedAllotment(value);
    setSelectedLot((prev) => ({ ...prev, uuid_allotment: value }));
    setSelectedReferenceStatus('');
    setSelectedContractFilter('');
  };

  useEffect(() => {
    if (!selectedAllotment && allotments.length > 0) {
      const defaultAllotment = allotments[0].uuid;
      setSelectedAllotment(defaultAllotment);
      setSelectedLot((prev) => ({ ...prev, uuid_allotment: defaultAllotment }));
    }
  }, [allotments, selectedAllotment]);

  const handleOpenDialog = () => {
    setSelectedLot({ ...INITIAL_LOT_STATE, uuid_allotment: selectedAllotment });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = (lotData) => {
    const payload = lotData || selectedLot;
    if (editMode) {
      updateMutation.mutate(
        { id: payload.uuid, lotData: payload },
        { onSuccess: handleCloseDialog }
      );
      return;
    }

    createMutation.mutate(payload, { onSuccess: handleCloseDialog });
  };

  const handleEdit = (lot) => {
    setSelectedLot({ ...lot, status: normalizeLotStatusValue(lot.status) });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = (lot) => {
    setLotToDelete(lot);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!lotToDelete) return;
    try {
      await deleteMutation.mutateAsync(lotToDelete.uuid);
      setDeleteConfirmOpen(false);
      setLotToDelete(null);
    } catch (error) {
      console.error('Error deleting lot:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setLotToDelete(null);
  };

  const handleUpdateLot = (lot) => {
    if (!lot?.uuid) return;
    updateMutation.mutate(
      { id: lot.uuid, lotData: lot },
      { onSuccess: handleCloseDialog }
    );
  };

  return {
    allotments,
    allAllotments,
    allLots,
    selectedAllotmentLots: lots,
    projects,
    contracts,
    constructionTypes,
    isAllLotsLoading,
    monthlyProgressOverview,
    isMonthlyProgressOverviewLoading,
    selectedAllotment,
    selectedReferenceStatus,
    selectedContractFilter,
    referenceStatusOptions,
    contractOptions,
    handleSelectAllotment,
    handleReferenceStatusChange: (event) => setSelectedReferenceStatus(event.target.value),
    handleContractFilterChange: (event) => setSelectedContractFilter(event.target.value),
    data: paginatedData,
    isLoading:
      isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedLot,
    selectedProject,
    selectedContract,
    subscribers: selectedContract?.subscribers || [],
    deleteConfirmOpen,
    lotToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,
    handleEdit,
    handleUpdateLot,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
